import React, { type FC } from 'react';

import { useField } from 'formik';

import { Box } from '@clutter/clean';

import { Button, Table } from '@shared/components/bootstrap';
import { useToggle } from '@shared/hooks/use_toggle';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { Panel } from '@admin/components/helpers/panel';
import type { Landing__Location__TopicInput } from '@admin/schema';

import { LandingLocationTopicModal } from './modal';

const LandingLocationTopicDeleteButton: FC<{
  topic: Landing__Location__TopicInput;
  onDelete(): void;
}> = ({ topic, onDelete }) => {
  const { confirmation, confirm } = useConfirmation();
  const onClick = async () => {
    const confirmed = await confirm({
      title: <>Are you sure you want to delete {topic.name}?</>,
      description: <>Deleting is permanent.</>,
    });
    if (confirmed !== ConfirmationResponse.Confirm) return;
    onDelete();
  };

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      <Button kind="danger" onClick={onClick}>
        Delete
      </Button>
    </>
  );
};

const LandingLocationTopicEditButton: FC<{
  topic: Landing__Location__TopicInput;
  onSave(_: Landing__Location__TopicInput): void;
}> = ({ topic, onSave }) => {
  const { visible, onToggle } = useToggle();
  return (
    <>
      <Button kind="primary" onClick={() => onToggle()}>
        Edit
      </Button>
      {visible && (
        <LandingLocationTopicModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
          topic={topic}
        />
      )}
    </>
  );
};

const LandingLocationTopicNewButton: FC<{
  onSave(_: Landing__Location__TopicInput): void;
}> = ({ onSave }) => {
  const { visible, onToggle } = useToggle();
  return (
    <>
      <Button kind="primary" onClick={() => onToggle()}>
        Add
      </Button>
      {visible && (
        <LandingLocationTopicModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
        />
      )}
    </>
  );
};

export const LandingLocationTopicsPanel: FC = () => {
  const [field, _, helpers] = useField<Array<Landing__Location__TopicInput>>('topics');

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>FAQ</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <Table striped>
          <thead>
            <tr>
              <th className="text-left">Question</th>
              <th className="text-left">Answer</th>
              <th className="col-zero" />
            </tr>
          </thead>
          <tbody>
            {field.value.map(
              (topic, index) =>
                !topic._destroy && (
                  <tr key={index}>
                    <td className="text-left">{topic.name}</td>
                    <td className="text-left">
                      <div dangerouslySetInnerHTML={{ __html: topic.blurb }} />
                    </td>
                    <td className="col-zero">
                      <Box.Flex gap="4px">
                        <LandingLocationTopicEditButton
                          topic={topic}
                          onSave={(input) => {
                            const topics = [...field.value];
                            topics[index] = {
                              ...topics[index],
                              ...input,
                            };
                            helpers.setValue(topics);
                          }}
                        />
                        <LandingLocationTopicDeleteButton
                          topic={topic}
                          onDelete={() => {
                            const topics = [...field.value];
                            topics[index] = {
                              ...topics[index],
                              _destroy: !topics[index]._destroy,
                            };
                            helpers.setValue(topics);
                          }}
                        />
                      </Box.Flex>
                    </td>
                  </tr>
                ),
            )}
          </tbody>
        </Table>
      </Panel.Body>
      <Panel.Footer align="right">
        <LandingLocationTopicNewButton
          onSave={(input) => {
            const topics = [...field.value];
            topics.push(input);
            helpers.setValue(topics);
          }}
        />
      </Panel.Footer>
    </Panel>
  );
};
